import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { listAllOrgs, formatCAD, type AdminOrgRow } from '@/lib/adminBilling';
import { humanize } from '@/lib/errors';
import { formatDate } from '@/lib/dates';
import { freezeOrg, unfreezeOrg } from '@/lib/admin';

/**
 * /admin/orgs/:id — single org detail. Read-only summary plus freeze/unfreeze
 * (existing) and a deep-link to the subscription editor.
 */
export default function OrgDetail() {
  const { id } = useParams<{ id: string }>();
  const [row, setRow] = useState<AdminOrgRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const all = await listAllOrgs();
      setRow(all.find((r) => r.organization_id === id) ?? null);
    } catch (e) { setError(humanize(e)); }
    finally { setLoading(false); }
  }, [id]);

  useEffect(() => { refresh(); }, [refresh]);

  async function freeze() {
    if (!id) return;
    const reason = prompt('Reason for freezing the org?'); if (reason === null) return;
    setBusy(true); setError(null);
    try { await freezeOrg(id, reason || null); refresh(); }
    catch (e) { setError(humanize(e)); }
    finally { setBusy(false); }
  }
  async function unfreeze() {
    if (!id) return;
    setBusy(true); setError(null);
    try { await unfreezeOrg(id); refresh(); }
    catch (e) { setError(humanize(e)); }
    finally { setBusy(false); }
  }

  if (loading) return <p className="text-sm text-muted-foreground">Loading org…</p>;
  if (error)   return <p className="text-sm text-red-600">{error}</p>;
  if (!row)    return <p className="text-sm text-muted-foreground">Org not found.</p>;

  return (
    <div className="space-y-6">
      <header className="space-y-1">
        <Link to="/admin/orgs" className="text-xs underline text-muted-foreground">← All organizations</Link>
        <h2 className="text-2xl font-semibold tracking-tight">{row.display_name}</h2>
        <p className="text-sm text-muted-foreground">
          {row.legal_name} · {row.timezone} · joined {formatDate(row.org_created_at)}
        </p>
      </header>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Legal address</h3>
        {row.address_line1 || row.city ? (
          <address className="text-sm not-italic" style={{ color: 'hsl(var(--brand-navy))' }}>
            {row.address_line1 && <div>{row.address_line1}</div>}
            {row.address_line2 && <div>{row.address_line2}</div>}
            <div>
              {[row.city, row.province, row.postal_code].filter(Boolean).join(', ')}
            </div>
            {row.phone && <div className="mt-1 text-muted-foreground">{row.phone}</div>}
          </address>
        ) : (
          <p className="text-sm text-muted-foreground italic">No address on file. Owner can add via /owner/settings (Phase B).</p>
        )}
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Primary owner contact</h3>
        <dl className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
          <Stat label="Owner name"  value={row.owner_name  ?? '—'} />
          <Stat label="Owner email" value={row.owner_email ?? '—'} />
          <Stat label="Owner phone" value={row.owner_phone ?? '—'} />
        </dl>
      </section>

      <section className="rounded-lg border border-border bg-card p-4 space-y-3">
        <h3 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">Summary</h3>
        <dl className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Stat label="Org status" value={row.org_status} />
          <Stat label="Locations" value={`${row.active_location_count} / ${row.location_count}`} />
          <Stat label="Staff" value={String(row.staff_count)} />
          <Stat label="Plan status" value={row.sub_status ?? '—'} />
          <Stat label="Tier" value={row.sub_tier ?? '—'} />
          <Stat label="Billing" value={row.billing_period ?? '—'} />
          <Stat label="MRR (est.)" value={formatCAD(Number(row.mrr_cad))} />
        </dl>
        <div className="flex gap-2 flex-wrap pt-1">
          <Link to={`/admin/subscriptions/${row.organization_id}`}
            className="rounded-md bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:opacity-90">
            Manage subscription
          </Link>
          {row.org_status === 'frozen' ? (
            <button disabled={busy} onClick={unfreeze}
              className="rounded-md border border-border px-3 py-1.5 text-sm hover:bg-muted/40 disabled:opacity-50">
              Unfreeze org
            </button>
          ) : (
            <button disabled={busy} onClick={freeze}
              className="rounded-md border-2 border-red-300 px-3 py-1.5 text-sm font-medium text-red-900 hover:bg-red-50 disabled:opacity-50">
              Freeze org
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="text-sm font-medium" style={{ color: 'hsl(var(--brand-navy))' }}>{value}</dd>
    </div>
  );
}
